import {
    CSSPathService,
    DateService,
    DocumentVisibilityTracker,
    PulseEnvironment,
    PulseNavigator,
} from '@pulse/browser-shared';
import { CLSAttribution, RUMEntry, RUMFilter } from '@pulse/common';
import { Observable, filter, first, from, map, merge, tap } from 'rxjs';
import { rumEntryMatch } from './match';

export class PerformanceTimelineService {
    private firstPageHide?: number;

    constructor(
        private env: PulseEnvironment,
        private cssPathService: CSSPathService,
        private date: DateService,
        visibilityTracker: DocumentVisibilityTracker,
    ) {
        if (!visibilityTracker.visible) {
            this.firstPageHide = date.hrtTimeOrigin;
        }

        visibilityTracker
            .onVisibilityChange()
            .pipe(
                filter((v) => !v),
                first(),
            )
            .subscribe(() => {
                this.firstPageHide = date.hrt();
            });
    }

    subscribe(filters: RUMFilter[], buffered = true): Observable<RUMEntry> {
        const streams = filters
            .filter((f) => this.entrySupported(f))
            .map((f) => this.getFilterStream(f, buffered));

        return merge(...streams);
    }

    getEntries(filters: RUMFilter[]) {
        const streams = filters
            .filter((f) => this.entrySupported(f))
            .map((f) => this.getFilterStream(f, false, true));

        return merge(...streams);
    }

    private getFilterStream(entryFilter: RUMFilter, buffered: boolean, oneTime?: boolean) {
        const win: typeof window = this.env.win as any;

        if (
            !win.performance.getEntries ||
            !win.performance.getEntriesByType ||
            !win.performance.timeOrigin
        ) {
            return from([]);
        }

        if (oneTime) {
            return this.readEntries(entryFilter);
        }

        if (typeof win.PerformanceObserver === 'function' && win.WeakSet) {
            return this.getFilterStreamNative(entryFilter, buffered);
        } else {
            return this.getFilterStreamLegacy(entryFilter, buffered);
        }
    }

    private readEntries(entryFilter: RUMFilter) {
        const entryType = typeof entryFilter === 'string' ? entryFilter : entryFilter.entryType;
        const seen = new WeakSet();
        const entries = this.env.win.performance.getEntriesByType(entryType);

        return from(entries).pipe(
            filter((e) => !seen.has(e)),
            tap((e) => seen.add(e)),
            map((e) => this.mapEntry(e)),
            filter((e) => rumEntryMatch(e, entryFilter)),
        );
    }

    private getFilterStreamNative(filter: RUMFilter, buffered: boolean) {
        return new Observable<RUMEntry>((observer) => {
            const seen = new WeakSet();

            const perfObserver = new this.env.win.PerformanceObserver((list) => {
                const entries = list.getEntries();

                for (const raw of entries) {
                    if (seen.has(raw)) {
                        continue;
                    }

                    seen.add(raw);

                    const entry = this.mapEntry(raw);

                    if (!rumEntryMatch(entry, filter)) {
                        continue;
                    }

                    observer.next(entry);
                }
            });

            const type = typeof filter === 'string' ? filter : filter?.entryType;

            perfObserver.observe({ type, buffered });

            return () => perfObserver.disconnect();
        });
    }

    private getFilterStreamLegacy(filter: RUMFilter, buffered: boolean) {
        return new Observable<RUMEntry>((observer) => {
            const perf = this.env.win.performance;
            const type = typeof filter === 'string' ? filter : filter?.entryType;
            let last = buffered ? 0 : perf.now();

            const timer = setInterval(() => {
                const entries = this.env.win.performance.getEntriesByType(type);
                let maxTime = last;

                for (const raw of entries) {
                    const entry = this.mapEntry(raw);

                    if (maxTime < raw.startTime) {
                        maxTime = raw.startTime;
                    }

                    if (raw.startTime < last) {
                        continue;
                    }

                    if (!rumEntryMatch(entry, filter)) {
                        continue;
                    }

                    observer.next(entry);
                }

                last = maxTime;
            }, 1000);

            return () => clearInterval(timer);
        });
    }

    private mapEntry(raw: any): RUMEntry {
        const connection = (this.env.win.navigator as PulseNavigator).connection as any;
        const initiator =
            (String(raw.initiatorType) + '').toLowerCase() === 'xmlhttprequest'
                ? 'xhr'
                : String(raw.initiatorType);

        let entry: RUMEntry = {
            time_origin: this.date.hrtTimeOrigin,
            details: raw.detail,
            name: raw.name,
            entry_type: raw.entryType,
            type: raw.type,
            protocol: raw.nextHopProtocol || undefined,
            task_duration: raw.duration,
            start_time: raw.startTime,
            end_time: raw.responseEnd ?? (raw.duration ? raw.startTime + raw.duration : undefined),
            initiator_type: initiator,
            worker_start: raw.workerStart,
            redirect_start: raw.redirectStart,
            redirect_end: raw.redirectEnd,
            fetch_start: raw.fetchStart,
            domain_lookup_start: raw.domainLookupStart,
            domain_lookup_end: raw.domainLookupEnd,
            connect_start: raw.connectStart,
            connect_end: raw.connectEnd,
            secure_connection_start: raw.secureConnectionStart,
            request_start: raw.requestStart,
            response_start: raw.responseStart,
            response_end: raw.responseEnd,
            transfer_size: raw.transferSize,
            encoded_body_size: raw.encodedBodySize,
            decoded_body_size: raw.decodedBodySize,
            unload_event_start: raw.unloadEventStart,
            unload_event_end: raw.unloadEventEnd,
            dom_interactive: raw.domInteractive,
            dom_content_loaded_event_start: raw.domContentLoadedEventStart,
            dom_content_loaded_event_end: raw.domContentLoadedEventEnd,
            dom_complete: raw.domComplete,
            load_event_start: raw.loadEventStart,
            load_event_end: raw.loadEventEnd,
            redirect_count: raw.redirectCount,
            processing_start: raw.processingStart,
            processing_end: raw.processingEnd,
            cancelable: raw.cancelable,
            render_blocking: raw.renderBlockingStatus
                ? raw.renderBlockingStatus === 'blocking'
                : undefined,

            had_recent_input: raw.hadRecentInput,
            last_input_time: raw.lastInputTime,
            cls_value: raw.value,
            before_page_hide: this.beforePageHide(raw.startTime),
        };

        if (Array.isArray(raw.serverTiming) && raw.serverTiming.length) {
            entry.server_timing = raw.serverTiming;
        }

        if (Array.isArray(raw.workerTiming) && raw.workerTiming.length) {
            entry.worker_timing = raw.workerTiming;
        }

        if (connection && (entry.entry_type === 'resource' || entry.entry_type === 'navigation')) {
            entry = {
                ...entry,
                connection_downlink: connection.downlink,
                connection_rtt: connection.rtt,
                connection_type: connection.effectiveType,
                data_saving_mode: connection.saveData,
            };
        }

        if (Array.isArray(raw.attribution)) {
            // For longtask
            entry.attribution = raw.attribution.map((i: any) => ({
                name: i?.name,
                entry_type: i?.entryType,
                start_time: i?.startTime,
                duration: i?.duration,
                container_type: i?.containerType,
                container_src: i?.containerSrc,
                container_id: i?.containerId,
                container_name: i?.containerName,
            }));
        }

        if (entry.entry_type === 'layout-shift' && Array.isArray(raw.sources)) {
            entry.cls_sources = raw.sources
                .map((s: any) => this.createCLSAttribution(s))
                .filter(Boolean);
        }

        return entry;
    }

    private beforePageHide(startTime: number | undefined | null) {
        if (!this.firstPageHide) {
            return true;
        }

        if (!startTime) {
            return undefined;
        }

        return startTime < this.firstPageHide;
    }

    private entrySupported(filters: RUMFilter) {
        const list = this.env.win.PerformanceObserver?.supportedEntryTypes || [];
        const entryType = typeof filters === 'string' ? filters : filters.entryType;

        return list.indexOf(entryType) > -1;
    }

    private createCLSAttribution(source: any): CLSAttribution | null {
        if (typeof source !== 'object' || typeof source.node !== 'object') {
            return null;
        }

        const node: Element = source.node;
        const attribution: CLSAttribution = {
            current_rect: source.currentRect,
            previous_rect: source.previousRect,
        };

        if (node) {
            const path = this.cssPathService.cssPath(node);
            attribution.node = { path };
        }

        return attribution;
    }
}
